import { Button } from '@Utilities/Button';
import { TagManager } from '@ThirdParty/TagManager';
import { Fancyapps } from "@Libraries/Fancyapps";
import { Sezzle } from "@ThirdParty/Sezzle";
import { Cart, ICartCallback, ICartProduct } from "@Utilities/Cart";
import { Form } from '@Utilities/Form';
import { TextResource } from '@Utilities/TextResource';
import { Validation } from "@Validation/Validation";
import { Fancybox } from 'typings/Fancyapps';
import "./_Purchase.scss";
import { CookieManager } from '@Utilities/CookieManager';

export class Purchase
{
	public BtnAddToCart: HTMLElement;
	public Cart: Cart;
	public CartCallback: ICartCallback;
	public CartItemOriginIdEl: HTMLInputElement;
	public Form: Form;
	public PriceEl: HTMLElement;
	public PriceStrike: HTMLElement;
	public ProductIdEl: HTMLInputElement;
	public ProductSizeIdSelector: string;
	public QuantityEl: HTMLInputElement;
	public Product: ICartProduct;
	public Type: string;

	private BtnSaveForLater: HTMLButtonElement;
	private SaveForLaterModal: Fancybox;

	constructor(form: HTMLFormElement)
	{
		this.Form = new Form(form);
		this.BtnAddToCart = this.Form.Element.querySelector("button[type='submit']") as HTMLElement;
		this.CartItemOriginIdEl = this.Form.Element.querySelector("input[name='CartItemOriginID']");
		this.ProductIdEl = this.Form.Element.querySelector("input[name='ProductID']");
		this.QuantityEl = this.Form.Element.querySelector("input[name='Quantity']");
		this.PriceEl = document.querySelector("#pnlPurchasePriceHeader .price:last-of-type");
		this.PriceStrike = document.querySelector("#pnlPurchasePriceHeader .price--strike");
		this.ProductSizeIdSelector = "input[type='Radio'][name='Size']:checked";
		this.Cart = new Cart();
		this.Type = "purchase";

		this.BindForm();
	}

	public AddProductToCart = (): void =>
	{
		if (this.BtnAddToCart !== null)
		{
			Button.StartLoading(this.BtnAddToCart);
		}

		this.Cart.AddProduct(this.Product, this.CartCallback);
	};

	public BindForm(): void
	{
		this.Form.ShowErrorHeader = false;
		this.Form.ValidationHandler = this.PurchaseValidator.bind(this);
		this.Form.SubmitHandler = this.AddProductToCart.bind(this);

		if (this.BtnAddToCart !== null)
		{
			Button.EnableLoading(this.BtnAddToCart);
		}

		this.BtnSaveForLater = document.getElementById("btnSaveProduct") as HTMLButtonElement;

		if (this.BtnSaveForLater !== null)
		{
			this.BtnSaveForLater.addEventListener("click", this.SaveForLaterHandler);
		}

		if (this.Form.Element !== null)
		{
			this.Form.Element.addEventListener("change", this.FormChange);
		}

		if (this.QuantityEl !== null)
		{
			this.QuantityEl.addEventListener("input", () =>
			{
				this.QuantityEl.setCustomValidity("");
			});

			this.QuantityEl.addEventListener("invalid", () =>
			{
				this.QuantityEl.setCustomValidity("");

				if (this.QuantityEl.validity.rangeOverflow)
				{
					this.QuantityEl.setCustomValidity(TextResource.FormatText("Feature_Shipping_MaxQuantity", [this.QuantityEl.getAttribute("max")]));
				}
			});
		}

		this.BindSezzleInfo();
	}

	private BindSezzleInfo = (): void =>
	{
		const sezzleInfo = document.getElementById("sezzleInfo");

		if (sezzleInfo !== null)
		{
			Sezzle.BindInformationLink(sezzleInfo);
		}
	};

	private FormChange = (): void =>
	{
		const selectedSize = this.Form.Element.querySelector("input[name='Size']:checked") as HTMLInputElement;
		const afterpayPlacement = document.querySelector("afterpay-placement");

		if (selectedSize)
		{
			const selectedLabel = selectedSize.closest("label");

			if (selectedLabel !== null)
			{
				if (afterpayPlacement !== null)
				{
					this.UpdateAfterPayPrice(selectedLabel.getAttribute("data-price"));
				}

				if (this.QuantityEl !== null)
				{
					const max = selectedLabel.getAttribute("data-qty");

					if (max !== "")
					{
						let valMsg = this.QuantityEl.getAttribute("data-val-range");

						if (valMsg !== "")
						{
							valMsg = valMsg.replace(/(\s)(\w+)(.$)/i, `$1${max}$3`);
						}

						this.QuantityEl.setAttribute("max", max);
						this.QuantityEl.setAttribute("data-val-range-max", max);
						this.QuantityEl.setAttribute("data-val-range", valMsg);
					}
				}

				if (this.PriceEl !== null)
				{
					this.UpdateDisplayPrice(selectedLabel);
				}
			}
		}
	};

	private UpdateAfterPayPrice = (price: string): void =>
	{
		const afterpayPlacement = document.querySelector("afterpay-placement");

		if (price !== "" || price !== null)
		{
			price = price.substring(1);
			afterpayPlacement.setAttribute("data-amount", price);
		}
	};

	private UpdateDisplayPrice = (selectedLabel: HTMLElement): void =>
	{
		const price = selectedLabel.getAttribute("data-price");
		const sale = selectedLabel.getAttribute("data-sale");

		if (price !== "" || price !== null && this.PriceEl !== null && this.PriceEl.innerText !== price)
		{
			this.PriceEl.innerText = price;

			if (sale === "true")
			{
				this.PriceEl.classList.add("price--sale");

				if (this.PriceStrike !== null)
				{
					this.PriceStrike.style.display = "";
				}
			}
			else
			{
				this.PriceEl.classList.remove("price--sale");

				if (this.PriceStrike !== null)
				{
					this.PriceStrike.style.display = "none";
				}
			}
		}
	};

	public GetCartProduct(): ICartProduct
	{
		const sizeEl = this.Form.Element.querySelector(this.ProductSizeIdSelector) as HTMLInputElement;
		let cartItemOriginId, price: string, productId, productSizeID, productSku, quantity;

		if (this.CartItemOriginIdEl !== null)
		{
			cartItemOriginId = this.CartItemOriginIdEl.value;
		}

		if (this.ProductIdEl !== null)
		{
			productId = this.ProductIdEl.value;
		}

		if (sizeEl !== null)
		{
			productSizeID = sizeEl.value;

			if (productSizeID === undefined || productSizeID === null || productSizeID === "")
			{
				productSizeID = (this.Form.Element.querySelector("input[name='Size']:checked") as HTMLInputElement).value;
			}

			productSku = sizeEl.getAttribute("data-sku");

			if (productSku === undefined || productSku === null || productSku === "")
			{
				productSku = (this.Form.Element.querySelector("input[name='Size']:checked") as HTMLInputElement).getAttribute("data-sku");
			}

			if (sizeEl.labels.length > 0)
			{
				price = sizeEl.labels[0].getAttribute("data-price");

				if (price === undefined || price === null || price === "")
				{
					const sizeChecked = this.Form.Element.querySelector("input[name='Size']:checked") as HTMLInputElement;

					if (sizeChecked.labels.length > 0)
					{
						price = sizeChecked.labels[0].getAttribute("data-price");
					}
				}

				const currencySymbol = CookieManager.GetCookie(CookieManager.Cookies.Currency.Key, CookieManager.Cookies.Currency.CurrencySymbol);
				price = price.replace(currencySymbol, "");
			}
		}

		if (this.QuantityEl !== null)
		{
			quantity = parseInt(this.QuantityEl.value);
		}

		const cartProduct: ICartProduct = {
			CartItemOriginID: cartItemOriginId,
			Price: parseFloat(price),
			ProductID: productId,
			ProductSizeID: productSizeID,
			ProductSku: productSku,
			Quantity: quantity
		};

		return cartProduct;
	}

	public PurchaseValidator = (): boolean =>
	{
		this.Product = this.GetCartProduct();

		if (!Validation.Quantity(this.Product.Quantity.toString()))
		{
			this.Form.Errors.push(TextResource.GetTextByCulture("Feature_Shipping_FuturePrchseQnty"));
		}

		if (this.Product.ProductSizeID == "" || this.Product.ProductSizeID == null || this.Product.ProductSizeID == undefined)
		{
			this.Form.Errors.push(TextResource.GetTextByCulture("Feature_Theme_SelectSize"));
		}

		if (this.Form.Errors.length > 0)
		{
			return false;
		}
		else
		{
			return true;
		}
	};

	private SaveForLaterHandler = (e: Event): void =>
	{
		e.preventDefault();

		const isValid = this.Form.ValidateForm();

		if (isValid)
		{
			this.SaveForLaterSubmit();
		}
	};

	private SaveForLaterSubmit = async (): Promise<void> =>
	{
		const saveForLaterProduct = this.GetCartProduct();

		// Override quantity selection for Save For Later Items
		saveForLaterProduct.Quantity = 1;

		let url = "/account/save-for-later?md=1";
		let sep = "&";

		if (saveForLaterProduct.ProductID !== null && saveForLaterProduct.ProductID !== undefined)
		{
			url += sep + "pid=" + saveForLaterProduct.ProductID;
			sep = "&";
		}

		if (saveForLaterProduct.ProductSizeID !== null && saveForLaterProduct.ProductSizeID !== undefined)
		{
			url += sep + "mpid=" + saveForLaterProduct.ProductSizeID;
			sep = "&";
		}

		if (saveForLaterProduct.CartItemOriginID !== null && saveForLaterProduct.CartItemOriginID !== undefined)
		{
			url += sep + "cio=" + saveForLaterProduct.CartItemOriginID;
			sep = "&";
		}

		url = "/account/login?ReturnUrl=" + encodeURIComponent(url);

		const fancyapps = await Fancyapps.Import();
		this.SaveForLaterModal = new fancyapps.Fancybox(
			[
				{
					src: url,
					type: "iframe"
				}
			],
			{
				mainClass: "sfl-modal"
			}
		);

		window.addEventListener("message", this.SaveForWindowListener);

		let currency = CookieManager.GetCookie(CookieManager.Cookies.Currency.Key, CookieManager.Cookies.Currency.CurrencyCode);

		if (currency === "")
		{
			const currencyMeta = document.querySelector("meta[property='product:price:currency']") as HTMLMetaElement;
			currency = currencyMeta.content;
		}

		TagManager.CallEvent(TagManager.Events.SaveForLater, {
			currency: currency,
			value: saveForLaterProduct.Price,
			items: [{
				item_name: document.querySelector("h1").textContent,
				item_id: saveForLaterProduct.ProductSku,
				price: saveForLaterProduct.Price
			}]
		});
	};

	private SaveForWindowListener = (e: MessageEvent): void =>
	{
		if (e.data === "xfbx")
		{
			this.SaveForLaterModal.close();
			window.removeEventListener("message", this.SaveForWindowListener);
		}
	};
}